import React from "react";
import { GetServerSideProps, NextPage } from "next";
import Link from "next/link";
// import LeftAuthPanel from "../../components/LeftAuthPanel";
import { OrDivider } from "../register";
import GitHub from "../../Icons/GitHub";
import BrimbleLogo from "../../Icons/BrimbleLogo";
import config, { appId } from "../../config";
import { setAuthCookies } from "../../helpers";
import { useStoreContext } from "../../context";
import { useSWRConfig } from "swr";
import { Actions } from "../../context/reducer";
import { toast } from "sonner";
import useAbly from "../../hooks/useAbly";
import { useRouter } from "next/router";
import Seo from "../../components/Micro/Seo";
import Button from "@/components/Micro/Button";
import AuthLayout from "@/layouts/AuthLayout";
import { AuthProvider } from "@/enum/auth.enum";
import GoogleLogo from "@/Icons/Google";

const Login: NextPage = () => {
  const [isLoadingData, setIsLoadingData] = React.useState({
    isLoading: false,
    which: null as AuthProvider | null,
  });
  const router = useRouter();
  const { next, redirect_url } = router.query;
  const { dispatch } = useStoreContext();
  const { mutate } = useSWRConfig();
  const socket = useAbly();

  const handleOauth = (provider: AuthProvider) => {
    const url = `${config.AUTH_API_URL}/signin/${provider}?device=${appId}&type=signin`;
    if (provider) {
      setIsLoadingData({ isLoading: true, which: provider });
      window.open(`${url}`, "_blank", "width=600,height=600");
    }
    const channel = socket.channels.get(appId);
    channel.subscribe("auth", ({ data }: any) => {
      channel.unsubscribe("auth");

      setAuthCookies({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user_data: "",
      }).then(() => {
        setIsLoadingData({ isLoading: false, which: null });
        dispatch({
          type: Actions.AUTHENTICATE,
          payload: { is_authenticated: true, access_token: data.access_token },
        });
        if (data.first_name) {
          toast.success(`Welcome back ${data.first_name}`);
        } else {
          toast.success(`Welcome back`);
        }
        mutate(
          `${config.AUTH_API_URL}/user/me`,
          { data: data },
          { revalidate: false },
        );

        if (redirect_url) return window.open(`${redirect_url}`, "_parent");

        if (!data?.onboard?.user) {
          router.push(`/onboard${next ? `?next=${next}` : ""}`);
        } else if (next) {
          router.push(`${next}`);
        } else {
          router.push("/overview");
        }
      });
    });
  };

  return (
    <>
      <Seo title="Login" description="Authenticate to continue using Brimble" />

      <AuthLayout>
        <>
          <div className="flex w-full items-center justify-between">
            <div className="w-2/3">
              <h1 className="text-[40px] font-semibold text-primary-dark dark:text-white">
                Welcome Back
              </h1>
              <p className="w-2/3 text-sm text-gray-600 dark:text-gray-400">
                Choose your preferred method to sign in to your account.
              </p>
            </div>
            <Link href={"https://brimble.io"} passHref target="">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-light dark:bg-theme-color-light">
                <BrimbleLogo />
              </div>
            </Link>
          </div>
          <div className="w-full space-y-2 py-10">
            <Button
              className="gsi-material-button btn-ghost btn-block btn-lg btn border-none bg-light text-sm"
              onClick={() => handleOauth(AuthProvider.Google)}
              loading={
                isLoadingData.which === AuthProvider.Google &&
                isLoadingData.isLoading
              }
              light
            >
              <div
                className="gsi-material-button-content-wrapper"
                style={{ justifyContent: "center" }}
              >
                <div className="gsi-material-button-icon">
                  <GoogleLogo />
                </div>
                <span className="gsi-material-button-contents">
                  Continue with Google
                </span>
              </div>
            </Button>

            <Button
              className="btn-ghost btn-block btn-lg btn gap-2 bg-light text-sm capitalize"
              onClick={() => handleOauth(AuthProvider.Github)}
              loading={
                isLoadingData.which === AuthProvider.Github &&
                isLoadingData.isLoading
              }
              light
            >
              <GitHub className="text-black dark:text-white" />
              login with github
            </Button>
            <OrDivider />

            <Link href="/login/email">
              <a>
                <Button className="btn-primary btn-block btn-lg btn rounded-none text-sm capitalize text-white">
                  login with my email address
                </Button>
              </a>
            </Link>
            <Link href="/register">
              <a>
                <button className="btn-primary btn-link btn-block btn-lg btn mt-5 text-[14px] font-medium capitalize text-primary no-underline dark:text-gray-400">
                  {"Don't have an account? Register."}
                </button>
              </a>
            </Link>
          </div>
        </>
      </AuthLayout>
    </>
  );
};

export default Login;

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  if (req.cookies[config.ACCESS_TOKEN]) {
    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    };
  }

  return {
    props: {},
  };
};
